import {
  PusherClarityReasonFeedback,
  SetError,
  WtPusherEvent,
} from '@chegg-wt/core/state/paraphraser/types'
import {
  CLARITY_FEEDBACK_REASON_FETCH_RETRY_COUNT,
  CLARITY_FEEDBACK_REASON_TIMEOUT,
} from '@chegg-wt/core/constants/paraphraser'
import { oneGraph } from '@chegg-wt/core/api/oneGraph'
import { CheckTypeInput } from '@chegg-wt/core/globalTypesOneGraph'
import lruCache from '../lruCache'

type ClarityFeedbackReasonParams = {
  sentenceId: string
  originalSentence: { originalText: string; id: string }
  initiatedAt: string
  traceId: string
  paperId: string
  correctedSentence: string
  clarityCardId: string
  setError: SetError
}

class ClarityFeedbackReasonDataHandler {
  private sentenceId: string

  private timeout: ReturnType<typeof setTimeout>

  private retriesLeft: number

  private traceId: string

  private paperId: string

  private initiatedAt: string

  private clarityCardId: string

  private originalSentence: { originalText: string; id: string }

  private correctedSentence: string

  private onError: SetError

  public disableClarityReasonFeedbackAcceptance = () => {
    this.sentenceId = null
    this.initiatedAt = null
    this.originalSentence = null
    this.correctedSentence = null
    this.clarityCardId = null
    this.onError = null
    this.clearExistingTimeout()
  }

  public enableClarityFeedbackReason = ({
    sentenceId,
    originalSentence,
    initiatedAt,
    traceId,
    paperId,
    correctedSentence,
    clarityCardId,
    setError,
  }: ClarityFeedbackReasonParams) => {
    this.sentenceId = sentenceId
    this.originalSentence = originalSentence
    this.initiatedAt = initiatedAt
    this.traceId = traceId
    this.paperId = paperId
    this.correctedSentence = correctedSentence
    this.retriesLeft = CLARITY_FEEDBACK_REASON_FETCH_RETRY_COUNT
    this.clarityCardId = clarityCardId
    this.onError = setError
  }

  private clearExistingTimeout = () => {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  public handleClarityFeedbackReasonCall = async (
    correctedSentence: string,
    initiatedAt: string,
    originalSentence: { originalText: string; id: string },
    paperId: string,
    traceId: string
  ) => {
    // Clear old timeout for this reason
    this.clearExistingTimeout()

    const setError = this.onError // ? incorrect 'this' value inside timeout
    // Add a new timeout
    this.timeout = setTimeout(() => {
      this.disableClarityReasonFeedbackAcceptance()
      setError({
        code: 'TIMEOUT',
        message: 'Timeout in fetching clarity feedback reason',
      })
    }, CLARITY_FEEDBACK_REASON_TIMEOUT)

    await oneGraph
      .feedbackReason({
        request: {
          checkType: CheckTypeInput.CLARITY_REASON,
          correctedSentence,
          initiatedAt,
          originalSentence,
          paperId,
          traceId,
        },
      })
      .catch((error: Error) => {
        this.onError({ code: 'SERVER_ERROR', message: error.message })
        this.disableClarityReasonFeedbackAcceptance()
      })
  }

  private handleRetry(initiatedAt: string) {
    this.handleClarityFeedbackReasonCall(
      this.correctedSentence,
      initiatedAt,
      this.originalSentence,
      this.paperId,
      this.traceId
    )
    this.retriesLeft -= 1
  }

  public handleClarityFeedbackReasonEvent = async (
    eventData: WtPusherEvent,
    handleClarityFeedbackReasonData: (
      feedbackReason: string,
      clarityCardId: string
    ) => void
  ) => {
    // we will only consider clarity feedback reason events with same sentence id and same initiated time
    if (
      eventData.sentence.id === this.sentenceId &&
      eventData.initiatedAt === this.initiatedAt
    ) {
      if (eventData.error) {
        if (this.retriesLeft > 0) {
          this.handleRetry(eventData.initiatedAt)
          return
        }
        this.onError({ code: 'API_ERROR', message: eventData.error.message })
        this.disableClarityReasonFeedbackAcceptance()
        return
      }

      const feedback = eventData.feedback as PusherClarityReasonFeedback
      if (feedback) {
        lruCache.addClarityReasonToCache(
          this.originalSentence.originalText,
          feedback.reason
        )
        handleClarityFeedbackReasonData(feedback.reason, this.clarityCardId)
        this.disableClarityReasonFeedbackAcceptance()
      }
    }
  }
}

const clarityFeedbackReasonDataHandler = new ClarityFeedbackReasonDataHandler()
export default clarityFeedbackReasonDataHandler
